import React from 'react';
import { Paper, withStyles } from '@material-ui/core';
import { hasPermission } from '../../../sc-utilities';
import BillingAccountForm from '../BillingAccountForm';
import * as Yup from 'yup';
import { connect } from 'react-redux';
import countries from 'i18n-iso-countries';
import { states, getStatesList, getCountryList } from '../../../sc-utilities/CountriesAndUSstatesList';
import History from "../../../../History";

const styles = theme => ({
    root: {
        width: `98,9%`,
        height: 'auto',
        [theme.breakpoints.down('sm')]: {
            padding: 0,
            margin: 0,
            width: '100%'
        }
    },
    root2: {
        width: `90,9%`,
        height: 'auto',
        marginBottom: -1,
        marginTop: -1,
        paddingTop: 0,
        marginLeft: theme.spacing(1),
        marginRight: theme.spacing(1),
        [theme.breakpoints.down('sm')]: {
            padding: 0,
            margin: 0,
            width: '94%'
        }
    },
});

const billingModes = [
    { value: 'prepaid', displayValue: 'Prepaid' },
    { value: 'postpaid', displayValue: 'Contract' }
];

const accountStatuses = [
    { value: 'active', displayValue: 'Active' },
    {
        value: 'prepared',
        displayValue: 'Prepared'
    },
    { value: 'inactive', displayValue: 'Inactive' }
];

const termsOfPayment = [
    { value: 'dor', displayValue: 'Due on Receipt' },
    { value: 'pia', displayValue: 'Payment in advance' },
    { value: 'eom', displayValue: 'End of Month' },
    { value: 'cod', displayValue: 'Cash on Delivery' },
    { value: 'net7', displayValue: '7 days after invoice date' },
    { value: 'net10', displayValue: '10 days after invoice date' },
    { value: 'net15', displayValue: '15 days after invoice date' },
    { value: 'net30', displayValue: '30 days after invoice date' },
    { value: 'net60', displayValue: '60 days after invoice date' }
];

const AccountOverview = props => {
    const { classes, loading, billingAccount, userLanguage, users, activeCustomer, translations, accountTab, permissions, editBillingAccount } = props;
    const communication = billingAccount && billingAccount.Communication ? JSON.parse(billingAccount.Communication) : null;
    const language = userLanguage || 'en';

    try {
        let lang = userLanguage;
        if (userLanguage.includes("zh-")) {
            lang = "zh";
        }
        countries.registerLocale(require(`i18n-iso-countries/langs/${lang}.json`));
    } catch (error) {
        console.log(error)
    }

    const countryList = getCountryList(countries, language);
    const statesList = getStatesList(states);

    let accountValue =
        billingAccount && users
            ? users.filter(displayValue => {
                return billingAccount.AccountantID === displayValue.value;
            })[0]
            : null;

    let address = billingAccount && typeof billingAccount?.Address === 'string' ? JSON.parse(billingAccount.Address) : billingAccount?.Address || null;

    const submitHandler = async data => {
        const requestData = { ...data };
        requestData['AccountID'] = billingAccount.AccountID;
        const Address = {
            AddressLine1: requestData?.AddressLine1,
            AddressLine2: requestData?.AddressLine2,
            City: requestData?.City,
            State: requestData.State?.key ?? statesList.find((c) => c.value === requestData?.State)?.key,
            Country: requestData?.Country?.key ?? countryList.find((c) => c.value === requestData?.Country)?.key
        };

        const Communication = {
            Language: requestData.Language,
            PhoneNumber: requestData.PhoneNumber,
            Email: requestData.Email
        };
        requestData['AccountantID'] = requestData['Accountant'] ? requestData['Accountant'].value : null;
        requestData['Address'] =Address;
        requestData['Communication'] = JSON.stringify(Communication);
        const result = await editBillingAccount({ ...requestData, ActiveCustomerID: activeCustomer });
        if (result) {
            History.goBack();
        }
    };

    let initialValues = {
        BillingMode: billingAccount && billingAccount.BillingMode ? billingAccount.BillingMode : '',
        Status: billingAccount && billingAccount.Status ? billingAccount.Status : 'active',
        ActivationDate: billingAccount && billingAccount.ActivationDate ? billingAccount.ActivationDate : '',
        ExpiresAt: billingAccount && billingAccount.ExpiresAt ? billingAccount.ExpiresAt : '',
        BillingDay: billingAccount && billingAccount.BillingDay ? billingAccount.BillingDay : 1,
        CustomerOrder: billingAccount && billingAccount.CustomerOrder ? billingAccount.CustomerOrder : '',
        VAT: billingAccount && billingAccount.VAT ? billingAccount.VAT : '',
        TaxClassification: billingAccount && billingAccount.TaxClassification ? billingAccount.TaxClassification : '',
        TermsOfPayment: billingAccount && billingAccount.TermsOfPayment ? billingAccount.TermsOfPayment : '',
        Accountant: accountValue ? accountValue : '',
        Language: communication && communication.Language ? communication.Language : '',
        PhoneNumber: communication && communication.PhoneNumber ? communication.PhoneNumber : '',
        Email: communication && communication.Email ? communication.Email : '',
        AddressLine1: address && address.AddressLine1 ? address.AddressLine1 : '',
        AddressLine2: address && address.AddressLine2 ? address.AddressLine2 : '',
        City: address && address.City ? address.City : '',
        State: address && address.State ? states[address.State] : '',
        Country: address && address.Country ? countries.getName(address.Country, language) : ''
    };

    let formSettings = {
        Validation: {
            BillingMode: Yup.string().required('Required'),
            Status: Yup.string().required('Required'),
            ActivationDate: Yup.date()
                .required(),
            ExpiresAt: Yup.date()
                .required(),
            BillingDay: Yup.number().required('Required').min(1).max(28),
            CustomerOrder: Yup.string().notRequired('Required'),
            TermsOfPayment: Yup.string().notRequired('Required'),
            CustomerID: Yup.string().notRequired('Required'),
            Accountant: Yup.string().notRequired('Required'),
            Language: Yup.string().required('Required'),
            PhoneNumber: Yup.string().notRequired('Required'),
            Email: Yup.string().email().notRequired('Required'),
            CustomerAddress: Yup.boolean().notRequired(),
            AddressLine1: Yup.string().required('Required'),
            AddressLine2: Yup.string().notRequired(),
            City: Yup.string().required('Required'),
            State: Yup.string().when("Country", {
                is: "United States of America",
                then: Yup.string().required()
            }),
            Country: Yup.string().required('Required')
        },
        ButtonSubmit: hasPermission(permissions, 'billing_account_update')
            ? {
                Title: translations['save'] || 'SAVE',
                Color: 'primary'
            }
            : null
    };

    return (
        <Paper className={accountTab ? classes.root2 : classes.root} >
            {/* {accountTab ? props.accountSelectionDropDown : null} when handling mutiple accounts(Future) */}
            <BillingAccountForm
                countryList={countryList}
                usStatesList={statesList}
                initialValues={initialValues}
                translations={translations}
                formSettings={formSettings}
                billingModes={billingModes}
                accountStatuses={accountStatuses}
                termsOfPayment={termsOfPayment}
                users={users}
                editForm={true}
                handleSubmit={submitHandler}
                loading={loading}
            />
        </Paper>
    );
};


const mapStateToProps = state => {
    return {
        activeCustomer: state?.core?.login?.activeCustomer?.CustomerID ?? null,
        translations: state.translate && state.translate.translations ? state.translate.translations.activeTranslations || {} : {},
        userLanguage: state?.core?.login?.userSettings?.DefaultLanguage || 'en',
    };
};

export default connect(mapStateToProps, null)(withStyles(styles)(AccountOverview));
